import React from 'react';
import { BoxIcon, PackageOpenIcon, ClockIcon, CheckIcon } from 'lucide-react';
import { useFunnel } from '../../contexts/FunnelContext';
import { packingCatalog } from '../../data/packingCatalog';
import { usd } from '../../utils/format';
import type { StepErrors } from '../../utils/validation';
import type { StringKey } from '../../data/i18n';
import { ChoiceCard } from './ChoiceCard';
import { QuantityStepper } from './QuantityStepper';
import { StepHeading } from './StepHeading';
import { BrandPhoto } from './BrandPhoto';
import { HelpAnswers } from './HelpAnswers';
import { ServicesPicker } from './ServicesPicker';
import { TruckCrewPicker } from './TruckCrewPicker';
const protections: StringKey[] = ['protect1', 'protect2', 'protect3', 'protect4', 'protect5', 'protect6'];
export function StepPacking({
  errors
}: {
  errors: StepErrors;
}) {
  const {
    t,
    lang,
    data,
    update,
    track
  } = useFunnel();
  const choose = (v: 'self' | 'professional') => {
    update({
      packing: v
    });
    track('PackingSelected', {
      packing: v
    });
  };
  return <div data-id="element-830" data-mp="1elwqxcxamxkv">
      <StepHeading title={t('packTitle')} data-id="element-831" data-mp="1em6gx0ic94qk" />
      <div role="radiogroup" aria-label={t('packTitle')} className="grid gap-3 md:grid-cols-2" data-id="element-832" data-mp="1jo5m8fxn2bq5">
        <ChoiceCard size="lg" selected={data.packing === 'self'} onSelect={() => choose('self')} title={t('packSelf')} body={t('packSelfBody')} icon={<BoxIcon className="h-6 w-6" data-id="element-834" data-mp="1tf07pi3297aj" />} data-id="element-833" data-mp="1ol5rwpqktcgq" />
        <ChoiceCard size="lg" selected={data.packing === 'professional'} onSelect={() => choose('professional')} title={t('packPro')} body={t('packProBody')} icon={<PackageOpenIcon className="h-6 w-6" data-id="element-836" data-mp="22foo2jfxfjql" />} data-id="element-835" data-mp="1xf0mj3u2nki4" />
      </div>
      {errors.packing && <p className="mt-2 text-sm font-medium text-magenta-dark" data-id="element-837" data-mp="27d7yt9df1eds">{t(errors.packing)}</p>}
      <p className="mt-3 flex items-start gap-2 text-sm text-ink-600" data-id="element-838" data-mp="2caglnn9ie6ld">
        <ClockIcon className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" data-id="element-839" data-mp="cz70cpls2te8" />
        {t('packPrepNote')}
      </p>

      {data.packing === 'professional' && <section className="mt-8" data-id="element-840" data-mp="hzutphxhbmbm">
          <h2 className="font-display text-xl font-extrabold text-ink" data-id="element-841" data-mp="9wj3xe9typ05">{t('packProductsTitle')}</h2>
          <ul className="mt-3 divide-y divide-ink-200 rounded-2xl bg-white ring-1 ring-ink-200" data-id="element-842" data-mp="4yhiwnsugfm1">
            {packingCatalog.map(p => <li key={p.id} className="grid gap-1 p-3 sm:grid-cols-[1fr_auto] sm:items-center sm:gap-4" data-id="element-843" data-mp="jwtfyvk4pqdq">
                <div className="px-3 sm:px-1" data-id="element-844" data-mp="euuddnw7qxhu">
                  <p className="font-semibold text-ink" data-id="element-845" data-mp="sveu8eikij8p">{p.label[lang]}</p>
                  <p className="text-sm text-ink-500" data-id="element-846" data-mp="nwtueg78a1ks">
                    {p.description[lang]} · {p.price !== null ? usd(p.price, lang) : t('pricedByTeam')}
                  </p>
                </div>
                <QuantityStepper label="" value={data.packingProducts[p.id] ?? 0} onChange={n => update({
            packingProducts: {
              ...data.packingProducts,
              [p.id]: n
            }
          })} decLabel={`${t('decrease')} ${p.label[lang]}`} incLabel={`${t('increase')} ${p.label[lang]}`} max={p.kind === 'review' ? 1 : 20} data-id="element-847" data-mp="xmn57ezjqcnz" />
              </li>)}
          </ul>
          <p className="mt-3 flex items-start gap-2 text-sm text-ink-600" data-id="element-848" data-mp="1v7ey07tdp8xz">
            <ClockIcon className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" data-id="element-849" data-mp="1h7nczn4g2sah" />
            {t('packTimeNote')}
          </p>
        </section>}

      <section className="mt-10 grid gap-6 rounded-2xl bg-white p-5 ring-1 ring-ink-200 md:grid-cols-[1fr_1.1fr] md:p-6" data-id="element-850" data-mp="17ilyy2hgmkz5">
        <div className="grid grid-cols-2 gap-3" data-id="element-851" data-mp="1c9vnezr9vi9g">
          <BrandPhoto role="protected_furniture" alt="Furniture protected with padded blankets by our crew" className="aspect-square w-full rounded-xl" icon={<BoxIcon className="h-8 w-8" strokeWidth={1.5} data-id="element-853" data-mp="12j10rua46gnh" />} data-id="element-852" data-mp="1can7bdbibrjw" />
          <BrandPhoto role="fragile_packing" fallbackRoles={['wrapped_furniture']} alt="Belongings packed by our crew" className="aspect-square w-full rounded-xl" icon={<PackageOpenIcon className="h-8 w-8" strokeWidth={1.5} data-id="element-855" data-mp="tht1p9zrxbsc" />} data-id="element-854" data-mp="12hg15q8y07ir" />
        </div>
        <div data-id="element-856" data-mp="1vwigjsliuhts">
          <h2 className="font-display text-xl font-extrabold text-ink" data-id="element-857" data-mp="1vv7h24z3zw8h">{t('protectTitle')}</h2>
          <ul className="mt-4 grid gap-2.5" data-id="element-858" data-mp="1rvq8v3saskzm">
            {protections.map(k => <li key={k} className="flex gap-3 text-[15px] leading-snug text-ink-700" data-id="element-859" data-mp="1n35gm6p1ify7">
                <CheckIcon className="mt-0.5 h-4 w-4 shrink-0 text-lime-dark" strokeWidth={3} aria-hidden="true" data-id="element-860" data-mp="1i5dpvg04szvl" />
                {t(k)}
              </li>)}
          </ul>
          <p className="mt-4 text-sm text-ink-500" data-id="element-861" data-mp="186o6fisl77d8">{t('protectNote')}</p>
        </div>
      </section>

      <div className="mt-12 space-y-12 border-t border-ink-200 pt-10" data-id="element-862" data-mp="1452txyjysv3k">
        <ServicesPicker data-id="element-863" data-mp="1476zsm1fc46h" />
        <TruckCrewPicker data-id="element-864" data-mp="z5zuctmr7h2z" />
      </div>

      <HelpAnswers screen="stepPacking" className="mt-10" data-id="element-865" data-mp="z71cfi26t3u7" />
    </div>;
}